/**
 * Application Logger
 * Centralized logging utility for the ALM system
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  private formatMessage(level: LogLevel, message: string, context?: string): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` [${context}]` : '';
    return `${timestamp} ${level.toUpperCase()}${contextStr}: ${message}`;
  }

  debug(message: string, contextOrData?: string | any, data?: any): void {
    if (this.isDevelopment) {
      const { context, actualData } = this.parseLogParams(contextOrData, data);
      console.log(this.formatMessage('debug', message, context), actualData || '');
    }
  }

  info(message: string, contextOrData?: string | any, data?: any): void {
    const { context, actualData } = this.parseLogParams(contextOrData, data);
    console.info(this.formatMessage('info', message, context), actualData || '');
  }

  warn(message: string, contextOrData?: string | any, data?: any): void {
    const { context, actualData } = this.parseLogParams(contextOrData, data);
    console.warn(this.formatMessage('warn', message, context), actualData || '');
  }

  error(message: string, contextOrData?: string | any, errorOrData?: Error | any, data?: any): void {
    let context: string | undefined;
    let error: Error | undefined;
    let actualData: any;

    if (typeof contextOrData === 'string') {
      context = contextOrData;
      if (errorOrData instanceof Error) {
        error = errorOrData;
        actualData = data;
      } else {
        actualData = errorOrData;
      }
    } else {
      actualData = contextOrData;
    }

    console.error(this.formatMessage('error', message, context), error || '', actualData || '');
  }

  private parseLogParams(contextOrData?: string | any, data?: any): { context?: string; actualData?: any } {
    if (typeof contextOrData === 'string') {
      return { context: contextOrData, actualData: data };
    } else {
      return { context: undefined, actualData: contextOrData };
    }
  }

  // Legacy methods for compatibility
  auditLog(message: string, context?: any, error?: any, data?: any): void {
    this.info(message, typeof context === 'string' ? context : 'audit', typeof context === 'object' ? context : data);
  }

  security(message: string, data?: any): void {
    this.warn(message, 'security', data);
  }
}

// Create singleton instance
export const logger = new Logger();